/**
 * チバリヨ - P7 営業ダッシュボード (Phase 2-B-4)
 *
 * 営業メンバーが帰社後に PC で開く、自分視点のダッシュボード。
 * admin (粟村) も開ける (KPI 対象外のため左カラムは「チーム」表示になる)。
 *
 * 3カラム構成:
 *   - 左   : 自分の KPI (今月の進捗 + ペースバッジ) / 自分のアラート / メンバー比較
 *   - 中央 : タイムライン (mobile-s2-timeline.js をそのままマウント。
 *            いいね・コメント・⋯メニュー(編集/削除)すべて使える完全版)
 *   - 右   : 担当顧客一覧 (訪問途絶を上部に分離ハイライト。
 *            クリックでその顧客の日報履歴モーダル)
 *
 * KPI 対象判定 (P1 / S1 / S5 と共通の原則):
 *   - KPI 対象 = 「当月の goals が設定されているメンバー」
 *   - 自分が対象 → 自分の数値 / 対象外 (粟村・松尾佳) → 「チーム」(対象者合算)
 *   - 対象外の場合、自分向けアラート (日報未入力 / ペース危険) は表示しない
 *
 * 日報の編集:
 *   - S2 の onEditReport コールバックを受け、PC モーダル内に
 *     mountS3CreateForm (編集モード) をマウント (モバイルのサブ画面の代替)
 *   - 保存後はモーダルを閉じ、タイムライン reload + 左右カラム再計算
 *
 * クエリ設計 (rules / indexes 変更なし):
 *   - config/system, members, clients, holidays: 全件 getDocs (P1 と同方式)
 *   - reports (当月): date 範囲のみ → クライアントで KPI 対象者に絞り込み
 *   - goals (当月): yearMonth == 当月
 *   - 顧客履歴モーダル: client.clientId == 顧客ID の等価クエリのみ
 *     (単一フィールド自動インデックスで賄える) → クライアントで date/time 降順ソート
 *   - 日報検索: 当半期 = date 範囲クエリ / 全期間 = reports 全件 getDocs。
 *     フリーワード(顧客名/地域/担当者/内容/メモ)・メンバー・種別はクライアント側で絞り込み。
 *     エリアは datalist 候補付きの部分一致 (候補は日報 area から重複排除して生成)
 *   - タイムラインのクエリは mobile-s2-timeline.js 側 (既存インデックス使用)
 *
 * 注意: ペース計算・営業日カウントは P1 / S1 / S5 と重複実装 (4箇所目)。
 *       閾値や式の意味を変える時は 4 箇所すべてを直すこと。
 */

import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  addDoc,
  updateDoc,
  serverTimestamp
} from 'https://www.gstatic.com/firebasejs/10.13.0/firebase-firestore.js';
import {
  getFunctions,
  httpsCallable,
} from 'https://www.gstatic.com/firebasejs/10.13.0/firebase-functions.js';

import { db } from './firebase-init.js';
import { mountS2Timeline } from './mobile-s2-timeline.js';
import { mountS4Tasks } from './mobile-s4-tasks.js';
import { fetchVisitStatsByClient, applyVisitStats } from './client-visit-stats.js';
import { normalizeForSearch } from './text-normalize.js';

// 訪問途絶日数の既定値(顧客個別の noVisitDays が未設定のときに使う)
const DEFAULT_NO_VISIT_DAYS = 60;

// AI 今週の訪問リスト (Cloud Functions: asia-northeast1)
const functions = getFunctions(undefined, 'asia-northeast1');
const generateWeeklyVisitListFn = httpsCallable(functions, 'generateWeeklyVisitList');
const generateNextActionsFn = httpsCallable(functions, 'generateNextActions');
let aiVisitList = null;       // { recommendations, summary, mode, area }
let aiVisitLoading = false;
let aiVisitError = null;

// AI 次アクション取込 (日報→自動でタスク化)
let naSuggestLoading = false;   // 取込中フラグ (ボタン表示用)
let naSuggestError = null;
let nextActionReportIds = new Set(); // 既に next-action 化済みの sourceReportId (重複生成防止)

// 次アクション / 備忘録 の振り分け境界 (今日からの日数)
const ACTION_HORIZON_DAYS = 30;

// 効果シグナル → 表示タグ (Cloud Function の signals と対応)
const AI_SIGNAL_TAG = {
  'quote-followup': { label: '🔥見積フォロー', cls: 'is-hot' },
  'deal-active':    { label: '💬商談中',       cls: 'is-deal' },
  'overdue':        { label: '🚫途絶',         cls: 'is-overdue' },
  'untouched':      { label: '✨新規',         cls: 'is-new' },
};

// =====================================================================
// 画面状態 (P1 と同様、画面は同時に1つしか存在しない前提のモジュール状態)
// =====================================================================
let currentMember = null;     // 表示対象メンバー (プレビュー中はプレビュー対象)
let currentAuthMember = null; // 認証ユーザー本人 (書き込みの createdBy に使う)
let configData = null;
let allMembers = [];          // 全メンバー (検索のメンバー絞り込み用。KPI 判定とは無関係)
let kpiMembers = [];          // 当月 goals が設定されている sales メンバー (order 昇順)
let activeClients = [];       // active な顧客全件
let monthReports = [];        // 当月の日報 (KPI 対象者分のみ)
let monthGoals = new Map();   // memberId → { appointmentTarget, quoteTarget }
let holidayDates = new Set(); // 当月の休業日 (YYYY-MM-DD)
let isKpiTarget = false;

// 右カラム: 次アクション (タスクに統一。見積提出は自動でタスク化)
let myOpenTasks = [];           // 自分の未完了タスク (次アクション + 備忘録の元データ)
let quoteFollowMap = new Map(); // sourceReportId → { taskId, status }  見積フォロータスクの紐付け (重複生成防止)
let actionBusy = false;         // 次アクションの連打防止
const QUOTE_FOLLOW_WINDOW_DAYS = 45; // 見積提出を自動タスク化する参照期間 (日)

let s2Ctrl = null;            // タイムラインのコントローラ
let s4Ctrl = null;            // タスク一覧 (mountS4Tasks) のコントローラ。タスク表示に切替時に遅延マウント
let rightScope = 'mine';      // 右カラム顧客スコープ: 'mine' (自分の担当) | 'all' (全顧客)
let rightSearch = '';         // 右カラム顧客の検索文字列 (名前/カナ/所在地)
let activeModal = null;       // 開いているモーダル (多重防止)

// 日報検索の状態 (中央カラム内で完結。S2 本体には手を入れない)
let searchActive = false;     // 検索結果を表示中か (true の間は中央タイムラインを隠す)
let currentSearchRows = [];   // 現在の検索結果 (行クリック→編集モーダルの参照用)
let areaOptions = null;       // エリア絞り込みの候補 (日報 area から重複排除。セッション内キャッシュ)

// =====================================================================
// 公開関数
// =====================================================================
export async function renderSalesDashboardPage(container, sessionMember, authMember) {
  currentMember = sessionMember;
  // 認証ユーザー本人 (= request.auth に対応)。プレビュー中は sessionMember(表示対象) と
  // 別人になる。書き込みの createdBy はルール上 myMemberId() と一致が必須なので本人を使う。
  currentAuthMember = authMember || sessionMember;

  // 再マウント時はタイムライン・タスクを後始末
  if (s2Ctrl) {
    try { s2Ctrl.destroy(); } catch (e) { /* noop */ }
    s2Ctrl = null;
  }
  if (s4Ctrl) {
    try { s4Ctrl.destroy(); } catch (e) { /* noop */ }
    s4Ctrl = null;
  }
  rightScope = 'mine';
  rightSearch = '';
  closeActiveModal(true);
  searchActive = false;
  currentSearchRows = [];
  aiVisitList = null;
  aiVisitLoading = false;
  aiVisitError = null;
  myOpenTasks = [];
  quoteFollowMap = new Map();
  actionBusy = false;
  naSuggestLoading = false;
  naSuggestError = null;
  nextActionReportIds = new Set();

  container.innerHTML = `
    <div class="p7-content">
      <div class="p7-header">
        <h1>営業ダッシュボード</h1>
        <div class="p7-header-actions">
          <span id="p7-updated" class="p7-updated"></span>
          <button id="p7-refresh-btn" type="button" class="btn btn-ghost">更新</button>
        </div>
      </div>
      <div id="p7-error" class="p7-error" style="display:none;"></div>
      <div class="p7-grid">
        <div class="p7-col p7-col-left" id="p7-left">
          <div class="p7-loading">読み込み中…</div>
        </div>
        <div class="p7-col p7-col-center">
          <div class="p7-center-inner">
            <div class="p7-center-toggle" id="p7-center-toggle">
              <button type="button" class="p7-toggle-btn is-active" data-view="timeline">タイムライン</button>
              <button type="button" class="p7-toggle-btn" data-view="tasks">タスク</button>
            </div>
            <div class="p7-search-bar" id="p7-search-bar">
              <div class="p7-search-fields">
                <input type="search" id="p7-search-keyword" class="p7-search-input"
                       placeholder="キーワード (顧客名・地域・担当者・内容・メモ)" autocomplete="off">
                <select id="p7-search-period" class="p7-search-select" aria-label="期間">
                  <option value="half">当半期</option>
                  <option value="all">全期間</option>
                </select>
                <select id="p7-search-member" class="p7-search-select" aria-label="メンバー">
                  <option value="">全メンバー</option>
                </select>
                <input type="search" id="p7-search-area" class="p7-search-area-input" list="p7-area-list"
                       placeholder="エリア (部分一致・候補から選択可)" autocomplete="off" aria-label="エリア">
                <datalist id="p7-area-list"></datalist>
                <select id="p7-search-type" class="p7-search-select" aria-label="種別">
                  <option value="">種別すべて</option>
                  <option value="訪問">訪問</option>
                  <option value="見積提出のみ">見積提出のみ</option>
                </select>
              </div>
              <div class="p7-search-actions">
                <button type="button" id="p7-search-run" class="btn">検索</button>
                <button type="button" id="p7-search-clear" class="btn btn-ghost">クリア</button>
              </div>
            </div>
            <div id="p7-timeline"></div>
            <div id="p7-search-results" class="p7-search-results" style="display:none;"></div>
            <div id="p7-tasks-pane" class="p7-tasks-pane" style="display:none;">
              <div class="p7-tasks-bar">
                <button type="button" id="p7-task-create" class="btn">＋ タスク作成</button>
              </div>
              <div id="p7-tasks-mount"></div>
            </div>
          </div>
        </div>
        <div class="p7-col p7-col-right" id="p7-right">
          <div class="p7-loading">読み込み中…</div>
        </div>
      </div>
    </div>
    <div class="p7-toast-wrap" id="p7-toast-wrap"></div>
  `;

  document.getElementById('p7-refresh-btn').addEventListener('click', async () => {
    await loadAndRenderSides();
    if (s2Ctrl) s2Ctrl.onShow({ reload: true });
    if (s4Ctrl) s4Ctrl.onShow({ reload: true });
  });

  // 中央カラムの表示切替 (タイムライン / タスク)
  document.getElementById('p7-center-toggle').addEventListener('click', (ev) => {
    const btn = ev.target.closest('.p7-toggle-btn');
    if (!btn) return;
    switchCenterView(btn.dataset.view);
  });

  // タスク作成 (中央タスク表示時のボタン)
  document.getElementById('p7-task-create').addEventListener('click', () => openTaskCreateModal());

  // 検索バーの配線 (要素は毎回作り直されるためリスナー累積なし)
  document.getElementById('p7-search-run').addEventListener('click', () => runSearch());
  document.getElementById('p7-search-clear').addEventListener('click', () => clearSearch());
  document.getElementById('p7-search-keyword').addEventListener('keydown', (ev) => {
    if (ev.key === 'Enter') runSearch();
  });
  document.getElementById('p7-search-area').addEventListener('keydown', (ev) => {
    if (ev.key === 'Enter') runSearch();
  });

  // 中央タイムライン (左右の集計とは独立してマウント)
  mountTimeline();

  // 左右カラム
  await loadAndRenderSides();

  // メンバー絞り込みの選択肢を埋める (loadCoreData 後に allMembers が揃う)
  populateSearchMembers();
  // エリア絞り込みの候補を埋める (非同期・非ブロッキング。揃うまでは「エリアすべて」のみ)
  populateSearchAreas();
}

// =====================================================================
// 中央: タイムライン
// =====================================================================
function mountTimeline() {
  const el = document.getElementById('p7-timeline');
  if (!el) return;
  el.innerHTML = '';
  s2Ctrl = mountS2Timeline(el, {
    currentMember,
    showToast: showP7Toast,
    onEditReport: (report) => openEditModal(report),
    autoLoad: true, // P7: もっと見るボタンを出さず、スクロールで自動読込
  });
}

// =====================================================================
// 中央カラム: タイムライン / タスク の表示切替
//   - タスクは mountS4Tasks をそのまま遅延マウント (自分/全員スコープ・
//     メンバー別チップ・完了/編集/戻す操作すべて S4 と同一)
//   - 編集/作成は openTaskEditModal / openTaskCreateModal で P7 モーダルに
//     mountS4CreateForm をマウント (S2 の日報編集と同じ枠組み)
// =====================================================================
function switchCenterView(view) {
  if (view !== 'timeline' && view !== 'tasks') return;

  document.querySelectorAll('#p7-center-toggle .p7-toggle-btn').forEach((b) => {
    b.classList.toggle('is-active', b.dataset.view === view);
  });

  const searchBar = document.getElementById('p7-search-bar');
  const timeline = document.getElementById('p7-timeline');
  const searchResults = document.getElementById('p7-search-results');
  const tasksPane = document.getElementById('p7-tasks-pane');

  const showTasks = view === 'tasks';
  if (searchBar) searchBar.style.display = showTasks ? 'none' : '';
  // タイムライン側は検索中なら検索結果を、そうでなければタイムラインを表示
  if (timeline) timeline.style.display = showTasks ? 'none' : (searchActive ? 'none' : '');
  if (searchResults) searchResults.style.display = showTasks ? 'none' : (searchActive ? '' : 'none');
  if (tasksPane) tasksPane.style.display = showTasks ? '' : 'none';

  if (showTasks) mountTasks();
}

/** タスク一覧を遅延マウント (初回切替時のみ) */
function mountTasks() {
  if (s4Ctrl) {
    s4Ctrl.onShow({ reload: true });
    return;
  }
  const el = document.getElementById('p7-tasks-mount');
  if (!el) return;
  el.innerHTML = '';
  s4Ctrl = mountS4Tasks(el, {
    currentMember,
    showToast: showP7Toast,
    onEditTask: (task) => openTaskEditModal(task),
  });
}

// =====================================================================
// 左右カラム: データロード + 描画
// =====================================================================
async function loadAndRenderSides() {
  const leftEl = document.getElementById('p7-left');
  const rightEl = document.getElementById('p7-right');
  if (!leftEl || !rightEl) return;

  try {
    await loadCoreData();
    renderLeft(leftEl);
    renderRight(rightEl);
    updateTimestamp();
    // 次アクションは別クエリ。右カラム描画後に差し込む。
    // (日報→次アクションのAI生成は平日7時のスケジュール関数が全員分を実行。
    //  ダッシュボードを開いた時の自動AI取込は二重実行になるため行わず、手動ボタンのみ。)
    await loadActionData();
    refreshActionSections();
  } catch (err) {
    console.error('[チバリヨ P7] loadAndRenderSides 失敗:', err);
    showError('ダッシュボードの読み込みに失敗しました: ' + err.message);
    leftEl.innerHTML = '';
    rightEl.innerHTML = '';
  }
}

async function loadCoreData() {
  // config/system
  const cSnap = await getDoc(doc(db, 'config', 'system'));
  if (!cSnap.exists()) throw new Error('config/system が存在しません');
  configData = cSnap.data();

  const now = new Date();
  const yearMonth = formatYearMonth(now);
  const monthStart = `${yearMonth}-01`;
  const monthEnd = formatDate(new Date(now.getFullYear(), now.getMonth() + 1, 0));

  // members / clients / reports / goals / holidays + 訪問実績集計 を並列取得
  const [mSnap, cliSnap, rSnap, gSnap, hSnap, visitStats] = await Promise.all([
    getDocs(collection(db, 'members')),
    getDocs(collection(db, 'clients')),
    getDocs(query(
      collection(db, 'reports'),
      where('date', '>=', monthStart),
      where('date', '<=', monthEnd)
    )),
    getDocs(query(collection(db, 'goals'), where('yearMonth', '==', yearMonth))),
    getDocs(collection(db, 'holidays')),
    fetchVisitStatsByClient(),
  ]);

  // goals (当月) → KPI 対象判定
  monthGoals = new Map();
  for (const d of gSnap.docs) {
    const data = d.data();
    monthGoals.set(data.memberId, {
      appointmentTarget: data.appointmentTarget || 0,
      quoteTarget: data.quoteTarget || 0
    });
  }
  isKpiTarget = monthGoals.has(currentMember.memberId);

  // members → 全メンバー保持 (検索のメンバー絞り込み用) + KPI 対象メンバー抽出
  allMembers = mSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
  kpiMembers = allMembers
    .filter((m) => m.role === 'sales' && m.active !== false && m.chibariyoAccess !== false && monthGoals.has(m.id))
    .sort((a, b) => (a.order || 0) - (b.order || 0));

  // clients → active のみ。最終訪問日/訪問回数は日報(type==='訪問')から
  // 導出した実績で上書き (顧客マスタの lastVisitDate はライブ更新されないため)
  activeClients = cliSnap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((c) => c.active !== false);
  applyVisitStats(activeClients, visitStats);

  // reports → KPI 対象者分のみ
  const kpiMemberIds = new Set(kpiMembers.map((m) => m.id));
  monthReports = rSnap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((r) => kpiMemberIds.has(r.memberId));

  // holidays → 当月分のみ全日展開
  holidayDates = new Set();
  for (const d of hSnap.docs) {
    const h = d.data();
    const s = h.startDate;
    const e = h.endDate || h.startDate;
    if (!s) continue;
    const startD = new Date(s);
    const endD = new Date(e);
    for (let d2 = new Date(startD); d2 <= endD; d2.setDate(d2.getDate() + 1)) {
      const str = formatDate(d2);
      if (str >= monthStart && str <= monthEnd) holidayDates.add(str);
    }
  }
}

// =====================================================================
// 次アクションのデータ取得 + 見積の自動タスク化
//   - myOpenTasks      : 自分の未完了タスク (次アクション + 備忘録の元データ)
//   - quoteFollowMap   : 見積フォロータスク (kind==='quote-followup') を sourceReportId で索引
//   - 未タスク化の見積 (直近 QUOTE_FOLLOW_WINDOW_DAYS 日) は自動で確認タスク化する
//
//   クエリはいずれも複合インデックス不要:
//     tasks (memberId==, status==) / (memberId==, kind==) は等価2条件 → index merge
//     reports は date 範囲のみ (単一フィールド自動インデックス) で取得し、
//       memberId / quote はクライアント側で絞る (loadCoreData と同方式)。
//       ※ (memberId, date) は date DESC 方向しか索引がなく、date>= の昇順
//         インデックス要求を避けるため、あえて memberId を等価条件に入れない。
// =====================================================================
async function loadActionData() {
  const me = currentMember.memberId;
  const cutoff = formatDate(new Date(Date.now() - QUOTE_FOLLOW_WINDOW_DAYS * 86400000));

  const [openSnap, qfSnap, naSnap, repSnap] = await Promise.all([
    getDocs(query(collection(db, 'tasks'),
      where('memberId', '==', me), where('status', '==', 'open'))),
    getDocs(query(collection(db, 'tasks'),
      where('memberId', '==', me), where('kind', '==', 'quote-followup'))),
    getDocs(query(collection(db, 'tasks'),
      where('memberId', '==', me), where('kind', '==', 'next-action'))),
    getDocs(query(collection(db, 'reports'),
      where('date', '>=', cutoff))),
  ]);

  // 未完了タスク → 期限昇順 (期限なしは末尾)
  myOpenTasks = openSnap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .sort((a, b) => {
      const da = a.dueDate || '9999-99-99';
      const dbb = b.dueDate || '9999-99-99';
      return da < dbb ? -1 : (da > dbb ? 1 : 0);
    });

  // 見積フォロータスクを sourceReportId で索引 (open/done 両方)
  quoteFollowMap = new Map();
  for (const d of qfSnap.docs) {
    const t = d.data();
    if (t.sourceReportId) quoteFollowMap.set(t.sourceReportId, { taskId: d.id, status: t.status });
  }

  // 既に next-action 化済みの日報 (open/done 両方) → 取込時の重複生成を防ぐ
  nextActionReportIds = new Set();
  for (const d of naSnap.docs) {
    const t = d.data();
    if (t.sourceReportId) nextActionReportIds.add(t.sourceReportId);
  }

  // 未タスク化の見積提出 (quote=true / 45日以内 / 未削除 / 確認タスク未作成)
  const quotesNeedingTask = repSnap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((r) => r.memberId === me && r.quote === true && !r.quoteFollowDismissed)
    .filter((r) => !quoteFollowMap.has(r.id));

  // 全項目をタスクに統一: 未タスク化の見積は自動で「見積提出後の確認」タスクにする。
  // 重複は quoteFollowMap (open/done) で防ぐので、再ロードしても二重生成しない。
  if (quotesNeedingTask.length) {
    const due = formatDate(new Date(Date.now() + 3 * 86400000)); // 既定: 3日後
    try {
      await Promise.all(quotesNeedingTask.map((r) => addDoc(collection(db, 'tasks'), {
        memberId: me,
        memberName: currentMember.name || '',
        createdBy: currentAuthMember.memberId,
        createdByName: currentAuthMember.name || '',
        title: '見積提出後の確認',
        dueDate: due,
        timingLabel: '',
        client: {
          clientId: (r.client && r.client.clientId) || null,
          clientText: (r.client && r.client.text) || '',
        },
        note: r.note ? `見積提出(${r.date}): ${r.note}` : `見積提出(${r.date})のフォロー`,
        status: 'open',
        kind: 'quote-followup',
        sourceReportId: r.id,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      })));
      // 反映のため未完了タスクを取り直す
      const reSnap = await getDocs(query(collection(db, 'tasks'),
        where('memberId', '==', me), where('status', '==', 'open')));
      myOpenTasks = reSnap.docs.map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => {
          const da = a.dueDate || '9999-99-99';
          const dbb = b.dueDate || '9999-99-99';
          return da < dbb ? -1 : (da > dbb ? 1 : 0);
        });
    } catch (e) {
      console.error('[チバリヨ P7] 見積の自動タスク化に失敗:', e);
    }
  }
}

/** 次アクション (タスク + 見積提出フォロー統合) + アクション備忘録 を差し替える */
function refreshActionSections() {
  const naHost = document.getElementById('p7-next-actions');
  const memoHost = document.getElementById('p7-action-memo');
  if (naHost) naHost.innerHTML = renderNextActions();
  if (memoHost) memoHost.innerHTML = renderActionMemo();
}

/** 未完了タスクを「次アクション(≤30日/期限なし)」と「備忘録(>30日)」に振り分け */
function splitOpenTasks() {
  const horizon = formatDate(new Date(Date.now() + ACTION_HORIZON_DAYS * 86400000));
  const near = [];
  const far = [];
  for (const t of myOpenTasks) {
    if (t.dueDate && t.dueDate > horizon) far.push(t);
    else near.push(t);
  }
  return { near, far };
}

/** タスク行の共通描画 (次アクション / 備忘録 で共用) */
function renderTaskRow(t, today) {
  const kindTag = t.kind === 'quote-followup'
    ? '<span class="p7-na-tag is-quote">🔥見積</span>'
    : (t.kind === 'next-action' ? '<span class="p7-na-tag is-ai">💡AI</span>' : '');
  // 予定の表示: timingLabel (中旬/下旬等) 優先、なければ期限日
  const schedLabel = t.timingLabel || (t.dueDate ? formatDueShort(t.dueDate) : '');
  const overdue = !!t.dueDate && t.dueDate < today;
  const sched = schedLabel
    ? `<span class="p7-na-due ${overdue ? 'is-overdue' : ''}">${escapeHtml(schedLabel)}${overdue ? ' 超過' : ''}</span>`
    : '';
  const client = (t.client && t.client.clientText)
    ? `<span class="p7-na-client">${escapeHtml(t.client.clientText)}</span>` : '';
  return `
    <div class="p7-na-item">
      <button type="button" class="p7-na-check" data-action="complete-task" data-task-id="${escapeHtml(t.id)}" aria-label="完了にする"></button>
      <div class="p7-na-body" data-action="edit-task" data-task-id="${escapeHtml(t.id)}">
        <div class="p7-na-title">${kindTag}${escapeHtml(t.title || '(無題)')}</div>
        ${(sched || client) ? `<div class="p7-na-meta">${sched}${client}</div>` : ''}
      </div>
    </div>`;
}

/** "YYYY-MM-DD" → "6/20(金)" */
function formatDueShort(dateStr) {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-').map(Number);
  const wd = ['日', '月', '火', '水', '木', '金', '土'][new Date(y, m - 1, d).getDay()];
  return `${m}/${d}(${wd})`;
}

// 右カラム: 次アクション (タスク + 見積提出フォローを統合)。
//   - タスク: 期限なし or 30日以内の未完了 (✓で完了)
//   - 見積提出フォロー: 未タスク化の見積 (「タスクに追加」で確認タスク化)
//   ヘッダーの「日報から取込」で AI が日報を読み、自動でタスク化する。
function renderNextActions() {
  const today = formatDate(new Date());
  const { near } = splitOpenTasks();
  const importBtn = `
    <button type="button" class="btn btn-ghost" data-na-action="generate" ${naSuggestLoading ? 'disabled' : ''}>
      ${naSuggestLoading ? '取込中…' : '日報から取込'} <span class="p7-ai-badge">AI</span>
    </button>`;

  // 全項目タスクに統一 (見積提出も loadActionData で自動タスク化済み)。1つのスクロール一覧。
  let body;
  if (near.length) {
    body = `<div class="p7-na-scroll"><div class="p7-na-list">${near.map((t) => renderTaskRow(t, today)).join('')}</div></div>`;
  } else if (naSuggestError) {
    body = `<div class="p7-ai-error"><span>取込に失敗しました</span></div>`;
  } else {
    body = '<div class="p7-empty">未完了のアクションはありません。「日報から取込」で日報からAIが作成します。</div>';
  }

  return `
    <section class="p7-section p7-na-section">
      <div class="p7-ai-head">
        <h2 class="p7-section-title">次アクション <span class="p7-section-sub">(${near.length})</span></h2>
        ${importBtn}
      </div>
      ${body}
    </section>`;
}

// 右カラム: アクション備忘録 (30日より先の未完了タスク)。時間が経つと次アクションへ移る。
function renderActionMemo() {
  const today = formatDate(new Date());
  const { far } = splitOpenTasks();
  if (!far.length) return ''; // 該当なしのときはセクションごと出さない
  const items = far.map((t) => renderTaskRow(t, today)).join('');
  return `
    <section class="p7-section">
      <h2 class="p7-section-title">アクション備忘録 <span class="p7-section-sub">(30日より先・${far.length})</span></h2>
      <div class="p7-na-list">${items}</div>
    </section>`;
}

// 次アクション: タスクを完了にする (完了すると一覧から消える)
async function completeTaskFromDashboard(taskId) {
  if (actionBusy) return;
  actionBusy = true;
  try {
    await updateDoc(doc(db, 'tasks', taskId), {
      status: 'done',
      completedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    showP7Toast('タスクを完了しました', 'success');
    await loadActionData();
    refreshActionSections();
    if (s4Ctrl) s4Ctrl.onShow({ reload: true });
  } catch (e) {
    console.error('[チバリヨ P7] タスク完了失敗:', e);
    showP7Toast('完了処理に失敗しました', 'error');
  } finally {
    actionBusy = false;
  }
}

// =====================================================================
// AI: 日報を読んで次アクションを自動でタスク化
//   - 「日報から取込」ボタンで実行。候補選択はせず、抽出結果をそのまま tasks 化。
//   - dueDate = AI 概算の予定日。30日以内/期限なし → 次アクション、30日超 → 備忘録。
//   - sourceReportId で重複生成を防止 (1日報につき1次アクション)。
// =====================================================================

async function importNextActionsFromReports() {
  if (naSuggestLoading || actionBusy) return;
  naSuggestLoading = true;
  naSuggestError = null;
  refreshActionSections(); // ボタンを「取込中…」に
  try {
    const payload = {};
    // admin プレビュー中は対象メンバーを明示 (本人と表示対象が異なる場合)
    if (currentMember.memberId !== currentAuthMember.memberId) {
      payload.memberId = currentMember.memberId;
    }
    const result = await generateNextActionsFn(payload);
    const all = (result.data && result.data.candidates) || [];

    // 既にタスク化済みの日報 + 同一実行内の重複 を除外 (sourceReportId 単位)
    const seen = new Set();
    const toCreate = [];
    for (const c of all) {
      if (!c.action || !c.sourceReportId) continue;
      if (nextActionReportIds.has(c.sourceReportId) || seen.has(c.sourceReportId)) continue;
      seen.add(c.sourceReportId);
      toCreate.push(c);
    }

    if (toCreate.length === 0) {
      showP7Toast('新しい次アクションはありませんでした', 'info');
      return;
    }

    await Promise.all(toCreate.map((c) => addDoc(collection(db, 'tasks'), {
      memberId: currentMember.memberId,
      memberName: currentMember.name || '',
      createdBy: currentAuthMember.memberId,
      createdByName: currentAuthMember.name || '',
      title: c.action,
      dueDate: c.scheduledDate || null,
      timingLabel: c.timing || '',
      client: { clientId: c.clientId || null, clientText: c.clientName || '' },
      note: c.basis || '',
      status: 'open',
      kind: 'next-action',
      sourceReportId: c.sourceReportId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    })));

    showP7Toast(`${toCreate.length}件を次アクションに追加しました`, 'success');
    await loadActionData();
    if (s4Ctrl) s4Ctrl.onShow({ reload: true });
  } catch (e) {
    console.error('[チバリヨ P7] 次アクション取込失敗:', e);
    naSuggestError = e.message || 'エラーが発生しました';
    showP7Toast('取込に失敗しました: ' + (e.code || e.message || e), 'error');
  } finally {
    naSuggestLoading = false;
    refreshActionSections();
  }
}

// =====================================================================
// 左カラム: KPI / アラート / メンバー比較 / 顧客
// =====================================================================
function renderLeft(el) {
  const yearMonth = formatYearMonth(new Date());
  const targetIds = isKpiTarget ? [currentMember.memberId] : kpiMembers.map((m) => m.id);
  const isTeam = !isKpiTarget;

  const topHtml = targetIds.length === 0
    ? `
      ${renderMyAlerts()}
      <section class="p7-section">
        <h2 class="p7-section-title">今月の進捗</h2>
        <div class="p7-empty">当月の目標が設定されたメンバーがいません。目標設定 (P2) で登録すると表示されます。</div>
      </section>
    `
    : `
      ${renderMyAlerts()}
      ${renderKpiCard(targetIds, isTeam, yearMonth)}
      ${renderMemberCompare()}
    `;

  // メンバー比較の下に顧客パネル (旧右カラムから移設)
  el.innerHTML = `${topHtml}<div id="p7-clients-panel"></div>`;
  mountClientsPanel();
}

// 顧客パネル (スコープ切替 + 検索 + 一覧)。左カラム下部にマウント。
// #p7-right-body / rightScope / rightSearch / renderRightBody はそのまま流用。
function mountClientsPanel() {
  const host = document.getElementById('p7-clients-panel');
  if (!host) return;

  host.innerHTML = `
    <section class="p7-section p7-clients-control">
      <div class="p7-clients-head">
        <h2 class="p7-section-title">顧客</h2>
        <div class="p7-client-scope" role="tablist">
          <button type="button" class="p7-scope-btn ${rightScope === 'mine' ? 'is-active' : ''}" data-scope="mine">自分の担当</button>
          <button type="button" class="p7-scope-btn ${rightScope === 'all' ? 'is-active' : ''}" data-scope="all">全顧客</button>
        </div>
      </div>
      <input type="search" id="p7-client-search" class="p7-client-search-input"
             placeholder="名前・カナ・所在地で検索" autocomplete="off" value="${escapeHtml(rightSearch)}">
    </section>
    <div id="p7-right-body"></div>
  `;

  // 検索入力 (デバウンス)。要素は再生成されるためリスナー累積なし
  const searchInput = host.querySelector('#p7-client-search');
  if (searchInput) {
    let timer = null;
    searchInput.addEventListener('input', (e) => {
      const v = e.target.value;
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => { rightSearch = v; renderRightBody(); }, 200);
    });
  }

  // クリック委譲: スコープ切替 + 顧客行→日報履歴モーダル
  host.onclick = (ev) => {
    const scopeBtn = ev.target.closest('.p7-scope-btn');
    if (scopeBtn) {
      const s = scopeBtn.dataset.scope;
      if (s && s !== rightScope) {
        rightScope = s;
        host.querySelectorAll('.p7-scope-btn').forEach((b) => b.classList.toggle('is-active', b === scopeBtn));
        renderRightBody();
      }
      return;
    }
    const row = ev.target.closest('.p7-client-row');
    if (!row) return;
    const client = activeClients.find((c) => c.id === row.dataset.clientId);
    if (client) openClientHistoryModal(client);
  };

  renderRightBody();
}

/** 自分向けアラート (KPI 対象者のみ。日報未入力 / ペース危険) */
function renderMyAlerts() {
  if (!isKpiTarget) return '';

  const now = new Date();
  const me = currentMember.memberId;
  const alerts = [];

  // 日報未入力 (P1 computeNoReportMembers と同一判定。当月内の最終日報日ベース)
  const noReportDays = configData.alertNoReportDays || 3;
  const thresholdDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - noReportDays);
  const myReports = monthReports.filter((r) => r.memberId === me);
  const lastDate = myReports.length > 0
    ? myReports.map((r) => r.date).sort().reverse()[0]
    : null;
  if (!lastDate || new Date(lastDate) < thresholdDate) {
    alerts.push({
      icon: '📝',
      title: '日報未入力',
      detail: `${noReportDays}日以上 日報が入力されていません (最終: ${lastDate ? escapeHtml(lastDate) : '当月記録なし'})`,
    });
  }

  // ペース危険 (P1 computeDangerPaceMembers と同一判定。月初は判定スキップ)
  const judgeAfter = configData.alertPaceJudgeAfterDay || 15;
  if (now.getDate() >= judgeAfter) {
    const pacePtThreshold = configData.alertPacePtThreshold || -15;
    const pace = computeMemberPace(me);
    const minPt = Math.min(
      pace.apptPacePt === null ? Infinity : pace.apptPacePt,
      pace.quotePacePt === null ? Infinity : pace.quotePacePt
    );
    if (minPt !== Infinity && minPt < pacePtThreshold) {
      alerts.push({
        icon: '⚡',
        title: 'ペース危険',
        detail: `アポ: ${formatPt(pace.apptPacePt)} / 見積: ${formatPt(pace.quotePacePt)}`,
      });
    }
  }

  if (alerts.length === 0) return '';

  return `
    <section class="p7-section p7-alerts">
      ${alerts.map((a) => `
        <div class="p7-alert-row">
          <span class="p7-alert-icon">${a.icon}</span>
          <div class="p7-alert-text">
            <strong>${a.title}</strong>
            <span>${a.detail}</span>
          </div>
        </div>
      `).join('')}
    </section>
  `;
}

/** 今月の進捗カード (自分 or チーム) */
function renderKpiCard(targetIds, isTeam, yearMonth) {
  const actuals = actualsOf(targetIds);
  const targets = targetsOf(targetIds);
  const progress = computeBusinessDayProgress();
  const pace = computePacePts(actuals, targets, progress);
  const status = pace.minPt === null ? null : paceStatus(pace.minPt);
  const title = isTeam ? 'チーム進捗 (今月)' : '今月の進捗';

  return `
    <section class="p7-section">
      <div class="p7-section-header">
        <h2 class="p7-section-title">${title} <span class="p7-section-sub">(${yearMonth})</span></h2>
        ${status ? `<span class="p7-pace-badge is-pace-${status}">${paceLabel(status)}</span>` : ''}
      </div>
      ${renderMetricRow('アポ', actuals.appointment, targets.appointmentTarget, pace.apptPacePt)}
      ${renderMetricRow('見積', actuals.quote, targets.quoteTarget, pace.quotePacePt)}
      <div class="p7-kpi-footer">
        <span>訪問 ${actuals.visit}件</span>
        <span class="p7-footer-sep">・</span>
        <span>営業日 ${progress.elapsed}/${progress.total}</span>
      </div>
    </section>
  `;
}

function renderMetricRow(label, actual, target, pacePt) {
  return `
    <div class="p7-metric">
      <div class="p7-metric-top">
        <span class="p7-metric-label">${escapeHtml(label)}</span>
        <span class="p7-metric-nums"><strong>${actual}</strong><span class="p7-metric-target"> / ${target || '—'}</span></span>
      </div>
      ${renderProgressBar(actual, target)}
      <div class="p7-metric-bottom">
        <span>${formatRate(actual, target)}</span>
        <span class="${pacePt !== null && pacePt < 0 ? 'is-minus' : ''}">${formatPt(pacePt)}</span>
      </div>
    </div>
  `;
}

/** メンバー比較 (KPI 対象メンバー全員のコンパクト行) */
function renderMemberCompare() {
  if (kpiMembers.length === 0) return '';

  return `
    <section class="p7-section">
      <h2 class="p7-section-title">メンバー比較</h2>
      <div class="p7-compare-list">
        ${kpiMembers.map((m) => {
          const myReports = monthReports.filter((r) => r.memberId === m.id);
          const appointment = myReports.filter((r) => r.type === '訪問' && r.hasAppointment === true).length;
          const quote = myReports.filter((r) => r.quote === true).length;
          const goal = monthGoals.get(m.id) || { appointmentTarget: 0, quoteTarget: 0 };
          const pace = computeMemberPace(m.id);
          const isMe = m.id === currentMember.memberId;
          return `
            <div class="p7-compare-row ${isMe ? 'is-me' : ''}">
              <div class="p7-compare-head">
                <span class="p7-compare-name">${escapeHtml(m.name)}${isMe ? '<span class="p7-me-chip">自分</span>' : ''}</span>
                <span class="p7-pace-badge p7-pace-badge-sm is-pace-${pace.status}">${paceLabel(pace.status)}</span>
              </div>
              <div class="p7-compare-nums">
                <span>アポ <strong>${appointment}</strong>/${goal.appointmentTarget}</span>
                <span>見積 <strong>${quote}</strong>/${goal.quoteTarget}</span>
              </div>
            </div>
          `;
        }).join('')}
      </div>
    </section>
  `;
}

// =====================================================================
// 右カラム上部: AI 今週の訪問リスト
// =====================================================================
function renderAiVisitSection() {
  const genBtn = `
    <button type="button" class="btn btn-ghost" data-ai-action="generate"
            ${aiVisitLoading ? 'disabled' : ''}>
      ${aiVisitList ? '更新' : '作成する'}
    </button>
  `;

  let body;
  if (aiVisitLoading) {
    body = `<div class="p7-ai-loading"><span class="p7-ai-spinner"></span>AIが今週の訪問案を作成中…</div>`;
  } else if (aiVisitError) {
    body = `
      <div class="p7-ai-error">
        <span>取得に失敗しました</span>
        <button type="button" class="btn btn-ghost" data-ai-action="generate">再試行</button>
      </div>
    `;
  } else if (!aiVisitList || !aiVisitList.recommendations || !aiVisitList.recommendations.length) {
    body = `<div class="p7-empty">「作成する」を押すと、AIが訪問履歴とエリアから今週の訪問案を提案します。</div>`;
  } else {
    const { recommendations, summary, weekStart, weekEnd } = aiVisitList;
    const items = recommendations.map((r) => {
      const signals = Array.isArray(r.signals) ? r.signals : [];
      const tags = signals.map((s) => {
        const t = AI_SIGNAL_TAG[s];
        return t ? `<span class="p7-ai-tag ${t.cls}">${t.label}</span>` : '';
      }).join('');
      const isHot = signals.includes('quote-followup');
      const next = r.nextAction ? `
        <div class="p7-ai-item-next">
          <span class="p7-ai-next-when">${escapeHtml(r.nextActionTiming || '時期未定')}</span>
          <span class="p7-ai-next-act">${escapeHtml(r.nextAction)}</span>
        </div>` : '';
      return `
        <div class="p7-ai-item ${isHot ? 'is-hot' : ''} ${r.isOverdue ? 'is-overdue' : ''}">
          <span class="p7-ai-rank">${r.rank}</span>
          <div class="p7-ai-item-body">
            <div class="p7-ai-item-name">${escapeHtml(r.clientName)}${tags}</div>
            <div class="p7-ai-item-area">${escapeHtml(r.area || '')}</div>
            <div class="p7-ai-item-reason">${escapeHtml(r.reason || '')}</div>
            ${next}
          </div>
        </div>
      `;
    }).join('');
    body = `
      <div class="p7-ai-result-head">
        <span class="p7-ai-week">${escapeHtml(weekStart || '')} 〜 ${escapeHtml(weekEnd || '')} ・ ${recommendations.length}件</span>
        <button type="button" class="p7-ai-close" data-ai-action="close" aria-label="閉じる">×</button>
      </div>
      ${items}
      ${summary ? `<div class="p7-ai-summary">${escapeHtml(summary)}</div>` : ''}
    `;
  }

  return `
    <section class="p7-section p7-ai-section">
      <div class="p7-ai-head">
        <h2 class="p7-section-title">今週の訪問案 <span class="p7-ai-badge">AI</span></h2>
        ${genBtn}
      </div>
      ${body}
    </section>
  `;
}

async function fetchAiVisitList() {
  aiVisitLoading = true;
  aiVisitError = null;
  refreshAiVisitSection();
  try {
    const result = await generateWeeklyVisitListFn({ mode: 'weekly' });
    aiVisitList = result.data;
  } catch (e) {
    console.error('[チバリヨ P7] AI 訪問案取得失敗:', e);
    aiVisitError = e.message || 'エラーが発生しました';
  } finally {
    aiVisitLoading = false;
    refreshAiVisitSection();
  }
}

/** AI セクションだけを差し替える (右カラム全体は再描画しない) */
function refreshAiVisitSection() {
  const host = document.getElementById('p7-ai-visit');
  if (host) host.innerHTML = renderAiVisitSection();
}

// =====================================================================
// 右カラム: 今週の訪問案 / 次アクション / アクション備忘録
// =====================================================================
function renderRight(el) {
  el.innerHTML = `
    <div id="p7-ai-visit">${renderAiVisitSection()}</div>
    <div id="p7-next-actions"><div class="p7-loading">読み込み中…</div></div>
    <div id="p7-action-memo"></div>
    <div id="p7-stale-clients">${renderStaleClients()}</div>
  `;

  // クリック委譲: 次アクション / AI 取込 / 今週の訪問案 / 訪問途絶 (onclick 上書きでリスナー累積を防止)
  el.onclick = (ev) => {
    // 次アクションの操作
    const actEl = ev.target.closest('[data-action]');
    if (actEl) {
      const action = actEl.dataset.action;
      if (action === 'complete-task') { completeTaskFromDashboard(actEl.dataset.taskId); return; }
      if (action === 'edit-task') {
        const t = myOpenTasks.find((x) => x.id === actEl.dataset.taskId);
        if (t) openTaskEditModal(t);
        return;
      }
    }

    // AI: 日報から次アクションを取込
    const naBtn = ev.target.closest('[data-na-action]');
    if (naBtn) {
      if (naBtn.dataset.naAction === 'generate') importNextActionsFromReports();
      return;
    }

    // 今週の訪問案
    const aiBtn = ev.target.closest('[data-ai-action]');
    if (aiBtn) {
      const action = aiBtn.dataset.aiAction;
      if (action === 'close') {
        aiVisitList = null;
        aiVisitError = null;
        refreshAiVisitSection();
        return;
      }
      // generate / retry
      aiVisitList = null;
      fetchAiVisitList();
      return;
    }

    // 訪問途絶の行 → 日報履歴モーダル
    const row = ev.target.closest('.p7-client-row');
    if (row) {
      const client = activeClients.find((c) => c.id === row.dataset.clientId);
      if (client) openClientHistoryModal(client);
    }
  };
}

/** 右カラム本体: スコープ + 検索に応じて顧客リストを描画 (#p7-right-body のみ更新) */
function renderRightBody() {
  const body = document.getElementById('p7-right-body');
  if (!body) return;

  const search = normalizeForSearch(rightSearch);
  const matchSearch = (c) => {
    if (!search) return true;
    const hay = normalizeForSearch([c.name, c.nameKana, c.area, c.prefecture, c.municipality]
      .filter(Boolean).join(' '));
    return hay.includes(search);
  };

  // 全顧客スコープ: フラットな一覧 (閲覧のみ。編集ボタンは履歴モーダル側で担当判定)
  if (rightScope === 'all') {
    const list = activeClients.filter(matchSearch)
      .sort((a, b) => (a.nameKana || a.name || '').localeCompare(b.nameKana || b.name || '', 'ja'));
    body.innerHTML = `
      <section class="p7-section">
        <h2 class="p7-section-title">全顧客 <span class="p7-section-sub">(${list.length}社)</span></h2>
        <div class="p7-client-list">
          ${list.length > 0
            ? list.map(renderClientRowAll).join('')
            : '<div class="p7-empty">該当する顧客がありません。</div>'}
        </div>
      </section>
    `;
    return;
  }

  // 自分の担当スコープ: 全担当顧客を1リストで (訪問途絶は右カラムに独立表示)。
  // 検索が効くよう途絶顧客もこの一覧に含め、途絶はインラインで is-stale 表示。
  const me = currentMember.memberId;
  const myClients = activeClients.filter((c) => c.primaryMemberId === me).filter(matchSearch);

  if (myClients.length === 0) {
    body.innerHTML = `
      <section class="p7-section">
        <h2 class="p7-section-title">担当顧客</h2>
        <div class="p7-empty">${search ? '該当する担当顧客がありません。' : '担当顧客が登録されていません。'}</div>
      </section>
    `;
    return;
  }

  const sorted = myClients
    .sort((a, b) => (a.nameKana || a.name || '').localeCompare(b.nameKana || b.name || '', 'ja'));

  body.innerHTML = `
    <section class="p7-section">
      <h2 class="p7-section-title">担当顧客 <span class="p7-section-sub">(${myClients.length}社)</span></h2>
      <div class="p7-client-list">
        ${sorted.map((c) => renderClientRow(c, isStaleClient(c))).join('')}
      </div>
    </section>
  `;
}

// 右カラム: 訪問途絶 (自分の担当で設定日数以上 未訪問)。独立表示・クリックで履歴モーダル。
function renderStaleClients() {
  const me = currentMember.memberId;
  const stale = activeClients
    .filter((c) => c.primaryMemberId === me && isStaleClient(c))
    .sort((a, b) => (a.lastVisitDate || '').localeCompare(b.lastVisitDate || '')); // 古い順
  if (!stale.length) return ''; // 該当なしは枠ごと非表示
  return `
    <section class="p7-section p7-stale-section">
      <h2 class="p7-section-title">🚫 訪問途絶 <span class="p7-section-sub">(設定日数以上・${stale.length}社)</span></h2>
      <div class="p7-client-list">
        ${stale.map((c) => renderClientRow(c, true)).join('')}
      </div>
    </section>
  `;
}

function refreshStaleClients() {
  const host = document.getElementById('p7-stale-clients');
  if (host) host.innerHTML = renderStaleClients();
}

/** 全顧客スコープの行 (所在地 + 担当者を表示。クリックで履歴モーダル) */
function renderClientRowAll(c) {
  const me = currentMember.memberId;
  const area = c.area || [c.prefecture, c.municipality].filter(Boolean).join('');
  let owner;
  if (c.primaryMemberId === me) owner = '自分';
  else if (!c.primaryMemberId) owner = '未割当';
  else {
    const m = allMembers.find((x) => x.id === c.primaryMemberId);
    owner = m ? m.name : '他担当';
  }
  const meta = [];
  if (area) meta.push(escapeHtml(area));
  meta.push(`担当: ${escapeHtml(owner)}`);
  return `
    <button type="button" class="p7-client-row" data-client-id="${escapeHtml(c.id)}">
      <span class="p7-client-name">${escapeHtml(c.name || '(名前未設定)')}</span>
      <span class="p7-client-visit">${meta.join(' ・ ')}</span>
    </button>
  `;
}

function renderClientRow(c, isStale) {
  return `
    <button type="button" class="p7-client-row ${isStale ? 'is-stale' : ''}" data-client-id="${escapeHtml(c.id)}">
      <span class="p7-client-name">${escapeHtml(c.name || '(名前未設定)')}</span>
      <span class="p7-client-visit">${c.lastVisitDate ? `最終訪問: ${escapeHtml(c.lastVisitDate)}` : '訪問記録なし'}${isStale ? `(基準${resolveNoVisitDays(c)}日)` : ''}</span>
    </button>
  `;
}

// 顧客個別の訪問途絶日数(正の数なら採用、未設定なら既定 DEFAULT_NO_VISIT_DAYS)
function resolveNoVisitDays(client) {
  const v = client && client.noVisitDays;
  return (typeof v === 'number' && Number.isFinite(v) && v > 0) ? v : DEFAULT_NO_VISIT_DAYS;
}

// 訪問途絶判定 (P1 computeStaleClients と同一。顧客個別の訪問途絶日数を使う)
function isStaleClient(c) {
  if (!c.lastVisitDate) return true;
  const now = new Date();
  const threshold = resolveNoVisitDays(c);
  const thresholdStr = formatDate(
    new Date(now.getFullYear(), now.getMonth(), now.getDate() - threshold)
  );
  return c.lastVisitDate < thresholdStr;
}

// =====================================================================
// 日報検索 (中央カラム内で完結。期間で取得 → クライアントで絞り込み)
//   - rules / indexes 変更なし:
//     当半期 = date 範囲クエリ (単一フィールド範囲。既存 loadCoreData と同型)
//     全期間 = reports 全件 getDocs (数百件規模)
//   - フリーワード / メンバー / 種別はクライアント側で絞り込み
//   - 結果カードは P7 独自描画 (S2 本体には手を入れない)。
//     カードクリックで既存の編集モーダル (openEditModal) を開く
// =====================================================================

/** メンバー絞り込みの選択肢を埋める (active な全メンバー、order 昇順) */
function populateSearchMembers() {
  const sel = document.getElementById('p7-search-member');
  if (!sel) return;
  const opts = allMembers
    // 閲覧者(viewer)は日報を書かないため絞り込み候補から除外
    .filter((m) => m.active !== false && m.chibariyoAccess !== false && m.role !== 'viewer')
    .sort((a, b) => (a.order || 0) - (b.order || 0))
    .map((m) => `<option value="${escapeHtml(m.id)}">${escapeHtml(m.name || '(名前未設定)')}</option>`)
    .join('');
  sel.innerHTML = '<option value="">全メンバー</option>' + opts;
}

/** エリア絞り込みの候補を埋める (日報 area から重複排除。datalist へ) */
async function populateSearchAreas() {
  const list = document.getElementById('p7-area-list');
  if (!list) return;
  try {
    if (!areaOptions) {
      const snap = await getDocs(collection(db, 'reports'));
      const set = new Set();
      snap.docs.forEach((d) => {
        const a = (d.data().area || '').trim();
        if (a) set.add(a);
      });
      areaOptions = Array.from(set).sort((x, y) => x.localeCompare(y, 'ja'));
    }
    const cur = document.getElementById('p7-area-list');
    if (!cur) return; // 再マウントで消えている可能性
    cur.innerHTML = areaOptions.map((a) => `<option value="${escapeHtml(a)}"></option>`).join('');
  } catch (e) {
    console.error('[チバリヨ P7] エリア候補の取得失敗:', e);
    // 失敗時は候補なし。エリアは自由入力の部分一致として機能する
  }
}

/** 当半期の date 範囲を返す ("YYYY-MM-DD" の start / end) */
function currentHalfRange() {
  let start = configData && configData.halfStart;
  let end = configData && configData.halfEnd;

  // "YYYY-MM" 形式なら start は月初、end は月末に展開
  if (typeof start === 'string' && /^\d{4}-\d{2}$/.test(start)) start = `${start}-01`;
  if (typeof end === 'string' && /^\d{4}-\d{2}$/.test(end)) {
    const [y, m] = end.split('-').map(Number);
    const lastDay = new Date(y, m, 0).getDate();
    end = `${end}-${String(lastDay).padStart(2, '0')}`;
  }

  // 設定が取れない場合のフォールバック: 直近6ヶ月
  if (!start || !end) {
    const now = new Date();
    return {
      start: formatDate(new Date(now.getFullYear(), now.getMonth() - 5, 1)),
      end: formatDate(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
    };
  }
  return { start, end };
}

/** 中央表示をタイムライン → 検索結果に切り替え */
function showSearchPane() {
  const tl = document.getElementById('p7-timeline');
  const res = document.getElementById('p7-search-results');
  if (tl) tl.style.display = 'none';
  if (res) res.style.display = '';
  searchActive = true;
}

/** 中央表示を検索結果 → タイムラインに戻す */
function backToTimeline() {
  const tl = document.getElementById('p7-timeline');
  const res = document.getElementById('p7-search-results');
  if (tl) tl.style.display = '';
  if (res) { res.style.display = 'none'; res.innerHTML = ''; }
  searchActive = false;
  currentSearchRows = [];
}

/** クリア: 条件をリセットしてタイムラインに戻す */
function clearSearch() {
  const kw = document.getElementById('p7-search-keyword');
  const period = document.getElementById('p7-search-period');
  const member = document.getElementById('p7-search-member');
  const areaEl = document.getElementById('p7-search-area');
  const type = document.getElementById('p7-search-type');
  if (kw) kw.value = '';
  if (period) period.value = 'half';
  if (member) member.value = '';
  if (areaEl) areaEl.value = '';
  if (type) type.value = '';
  backToTimeline();
}

const SEARCH_RESULT_LIMIT = 300;

/** 検索実行 */
async function runSearch() {
  const kwEl = document.getElementById('p7-search-keyword');
  const periodEl = document.getElementById('p7-search-period');
  const memberEl = document.getElementById('p7-search-member');
  const areaEl = document.getElementById('p7-search-area');
  const typeEl = document.getElementById('p7-search-type');
  const res = document.getElementById('p7-search-results');
  if (!kwEl || !res) return;

  const keyword = kwEl.value.trim().toLowerCase();
  const period = periodEl.value;       // 'half' | 'all'
  const memberId = memberEl.value;     // '' = 全員
  const area = areaEl ? areaEl.value.trim().toLowerCase() : ''; // '' = すべて。部分一致
  const type = typeEl.value;           // '' = すべて | '訪問' | '見積提出のみ'

  showSearchPane();
  res.innerHTML = `
    ${searchHeaderHtml('検索中…')}
    <div class="p7-loading">読み込み中…</div>
  `;
  bindSearchBack(res);

  try {
    let qRef;
    if (period === 'all') {
      qRef = collection(db, 'reports');
    } else {
      const { start, end } = currentHalfRange();
      qRef = query(
        collection(db, 'reports'),
        where('date', '>=', start),
        where('date', '<=', end)
      );
    }
    const snap = await getDocs(qRef);
    let rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

    if (memberId) rows = rows.filter((r) => r.memberId === memberId);
    if (area) rows = rows.filter((r) => (r.area || '').toLowerCase().includes(area));
    if (type) rows = rows.filter((r) => r.type === type);
    if (keyword) {
      rows = rows.filter((r) => {
        const hay = [
          r.client && r.client.text,
          r.area,
          r.contact,
          r.act,
          r.note,
        ].filter(Boolean).join(' ').toLowerCase();
        return hay.includes(keyword);
      });
    }

    // 日付 → 時刻 降順
    rows.sort((a, b) => {
      const dc = String(b.date || '').localeCompare(String(a.date || ''));
      if (dc !== 0) return dc;
      return String(b.time || '').localeCompare(String(a.time || ''));
    });

    const total = rows.length;
    const shown = rows.slice(0, SEARCH_RESULT_LIMIT);
    currentSearchRows = shown;
    renderSearchResults(res, shown, total);
  } catch (e) {
    console.error('[チバリヨ P7] 検索失敗:', e);
    res.innerHTML = `
      ${searchHeaderHtml('検索エラー')}
      <div class="p7-empty">検索に失敗しました。時間をおいて再度お試しください。</div>
    `;
    bindSearchBack(res);
  }
}

function searchHeaderHtml(countText) {
  return `
    <div class="p7-search-results-header">
      <span class="p7-search-count">${escapeHtml(countText)}</span>
      <button type="button" class="btn btn-ghost p7-search-back">タイムラインに戻る</button>
    </div>
  `;
}

/** 「タイムラインに戻る」だけを配線 (検索中・エラー時の暫定表示用) */
function bindSearchBack(res) {
  res.onclick = (ev) => {
    if (ev.target.closest('.p7-search-back')) backToTimeline();
  };
}

function renderSearchResults(res, rows, total) {
  const countText = rows.length < total
    ? `検索結果 ${total}件（新しい順 ${rows.length}件を表示）`
    : `検索結果 ${total}件`;

  if (rows.length === 0) {
    res.innerHTML = `
      ${searchHeaderHtml('検索結果 0件')}
      <div class="p7-empty">該当する日報がありません。条件を変えて再検索してください。</div>
    `;
    bindSearchBack(res);
    return;
  }

  res.innerHTML = `
    ${searchHeaderHtml(countText)}
    <div class="p7-search-list">
      ${rows.map(renderSearchRow).join('')}
    </div>
  `;

  // 戻る / 行クリック→編集 を 1つの onclick で処理 (リスナー累積防止)
  res.onclick = (ev) => {
    if (ev.target.closest('.p7-search-back')) { backToTimeline(); return; }
    const row = ev.target.closest('.p7-search-row');
    if (!row) return;
    const report = currentSearchRows.find((r) => r.id === row.dataset.reportId);
    if (report) openEditModal(report);
  };
}

function renderSearchRow(r) {
  const chips = [];
  if (r.type === '訪問' && r.hasAppointment) chips.push('<span class="p7-chip p7-chip-appo">アポ</span>');
  if (r.quote) chips.push('<span class="p7-chip p7-chip-quote">見積</span>');
  const clientName = (r.client && r.client.text) || '';
  return `
    <button type="button" class="p7-search-row" data-report-id="${escapeHtml(r.id)}">
      <div class="p7-history-head">
        <span class="p7-history-date">${escapeHtml(r.date || '')} ${escapeHtml(r.time || '')}</span>
        <span class="p7-history-member">${escapeHtml(r.memberName || '')}</span>
        <span class="p7-type-badge ${r.type === '訪問' ? 'is-visit' : 'is-quote-only'}">${escapeHtml(r.type || '')}</span>
        ${chips.join('')}
      </div>
      ${clientName ? `<div class="p7-search-client">${escapeHtml(clientName)}</div>` : ''}
      ${r.act ? `<div class="p7-history-act">${escapeHtml(r.act)}</div>` : ''}
      ${r.note ? `<div class="p7-search-note"><span class="p7-search-note-label">所感</span>${escapeHtml(r.note)}</div>` : ''}
    </button>
  `;
}

// =====================================================================
// 顧客の日報履歴モーダル
// =====================================================================
async function openClientHistoryModal(client) {
  const modal = openModal({ title: client.name || '(名前未設定)', width: 560 });
  if (!modal) return;

  const areaText = [client.prefecture, client.municipality].filter(Boolean).join(' ');
  // 自分が主担当の顧客のみ編集可 (admin は全担当顧客を編集可)。
  // 右カラムは自分の担当顧客しか出さないため通常は常に true だが、防御的に判定する。
  const canEdit = client.primaryMemberId === currentMember.memberId
    || currentMember.role === 'admin';
  modal.bodyEl.innerHTML = `
    <div class="p7-history-meta">
      ${areaText ? `<span>${escapeHtml(areaText)}</span>` : ''}
      <span>${client.lastVisitDate ? `最終訪問: ${escapeHtml(client.lastVisitDate)}` : '訪問記録なし'}</span>
    </div>
    ${canEdit ? `
      <div class="p7-history-actions">
        <button type="button" class="btn btn-ghost p7-client-edit-btn">✏️ 登録情報を編集</button>
      </div>
    ` : ''}
    <div class="p7-history-list"><div class="p7-loading">読み込み中…</div></div>
  `;

  if (canEdit) {
    const editBtn = modal.bodyEl.querySelector('.p7-client-edit-btn');
    if (editBtn) {
      editBtn.addEventListener('click', () => {
        modal.close();
        openClientEditModal(client);
      });
    }
  }

  const listEl = modal.bodyEl.querySelector('.p7-history-list');

  try {
    // 等価クエリのみ (単一フィールド自動インデックス) → クライアントで降順ソート
    const snap = await getDocs(query(
      collection(db, 'reports'),
      where('client.clientId', '==', client.id)
    ));
    const rows = snap.docs
      .map((d) => d.data())
      .sort((a, b) => {
        const dc = String(b.date || '').localeCompare(String(a.date || ''));
        if (dc !== 0) return dc;
        return String(b.time || '').localeCompare(String(a.time || ''));
      })
      .slice(0, 50);

    if (rows.length === 0) {
      listEl.innerHTML = '<div class="p7-empty">この顧客の日報はまだありません。</div>';
      return;
    }

    listEl.innerHTML = rows.map((r) => {
      const chips = [];
      if (r.type === '訪問' && r.hasAppointment) chips.push('<span class="p7-chip p7-chip-appo">アポ</span>');
      if (r.quote) chips.push('<span class="p7-chip p7-chip-quote">見積</span>');
      return `
        <div class="p7-history-row">
          <div class="p7-history-head">
            <span class="p7-history-date">${escapeHtml(r.date || '')} ${escapeHtml(r.time || '')}</span>
            <span class="p7-history-member">${escapeHtml(r.memberName || '')}</span>
            <span class="p7-type-badge ${r.type === '訪問' ? 'is-visit' : 'is-quote-only'}">${escapeHtml(r.type || '')}</span>
            ${chips.join('')}
          </div>
          ${r.act ? `<div class="p7-history-act">${escapeHtml(r.act)}</div>` : ''}
        </div>
      `;
    }).join('');
  } catch (e) {
    console.error('[チバリヨ P7] 顧客履歴取得失敗:', e);
    listEl.innerHTML = '<div class="p7-empty">日報履歴の読み込みに失敗しました。</div>';
  }
}

// =====================================================================
// 顧客 登録情報の編集モーダル (client-edit-form.js をマウント)
//   - 自分が主担当の顧客のみ (履歴モーダルの編集ボタンから遷移)
//   - 保存後はローカル activeClients を更新して右カラムを再描画
//   - 主担当 / active / archive 系には触れない (ルールが sales をこれに制限)
// =====================================================================
async function openClientEditModal(client) {
  const modal = openModal({ title: '登録情報を編集', width: 480 });
  if (!modal) return;
  try {
    const { mountClientEditForm } = await import('./client-edit-form.js');
    const member = allMembers.find((m) => m.id === client.primaryMemberId);
    const ctrl = mountClientEditForm(modal.bodyEl, {
      client,
      primaryMemberName: member ? member.name : '(自分)',
      showToast: showP7Toast,
      onSaved: (updated) => {
        modal.close();
        showP7Toast('顧客情報を更新しました', 'success');
        // ローカル状態を更新して顧客一覧・訪問途絶だけ再描画 (全体再読込はしない)
        const idx = activeClients.findIndex((c) => c.id === client.id);
        if (idx >= 0) activeClients[idx] = { ...activeClients[idx], ...updated };
        renderRightBody();
        refreshStaleClients();
      },
      onCancel: () => modal.tryClose(),
    });
    modal.setCtrl(ctrl);
  } catch (e) {
    console.error('[チバリヨ P7] 顧客編集フォーム表示失敗:', e);
    modal.close();
    showP7Toast('編集画面の表示に失敗しました', 'error');
  }
}

// =====================================================================
// 日報編集モーダル (S3 編集モードをマウント)
// =====================================================================
async function openEditModal(report) {
  const modal = openModal({ title: '日報を編集', width: 480 });
  if (!modal) return;

  try {
    const { mountS3CreateForm } = await import('./mobile-s3-create.js');
    const ctrl = mountS3CreateForm(modal.bodyEl, {
      currentMember,
      initialReport: report,
      onSaved: async () => {
        modal.close();
        showP7Toast('日報を更新しました', 'success');
        if (s2Ctrl) s2Ctrl.onShow({ reload: true });
        await loadAndRenderSides(); // アポ・見積フラグの変更が KPI に反映される
        if (searchActive) await runSearch(); // 検索結果表示中なら結果も最新化
      },
    });
    modal.setCtrl(ctrl);
  } catch (e) {
    console.error('[チバリヨ P7] 編集フォーム表示失敗:', e);
    modal.close();
    showP7Toast('編集画面の表示に失敗しました', 'error');
  }
}

// =====================================================================
// タスク作成 / 編集モーダル (S4 作成フォームをマウント)
//   - 作成: initialTask なし。担当者セレクタ付き (既定=自分)
//   - 編集: initialTask あり。削除ボタン付き
//   - 保存/削除後はモーダルを閉じてタスク一覧を再取得
// =====================================================================
async function openTaskCreateModal() {
  const modal = openModal({ title: 'タスクを作る', width: 480 });
  if (!modal) return;
  try {
    const { mountS4CreateForm } = await import('./mobile-s4-create.js');
    const ctrl = mountS4CreateForm(modal.bodyEl, {
      currentMember,
      onSaved: () => {
        modal.close();
        showP7Toast('タスクを作成しました', 'success');
        if (s4Ctrl) s4Ctrl.onShow({ reload: true });
        loadActionData().then(refreshActionSections).catch(() => {});
      },
    });
    modal.setCtrl(ctrl);
  } catch (e) {
    console.error('[チバリヨ P7] タスク作成フォーム表示失敗:', e);
    modal.close();
    showP7Toast('作成画面の表示に失敗しました', 'error');
  }
}

async function openTaskEditModal(task) {
  const modal = openModal({ title: 'タスクを編集', width: 480 });
  if (!modal) return;
  try {
    const { mountS4CreateForm } = await import('./mobile-s4-create.js');
    const ctrl = mountS4CreateForm(modal.bodyEl, {
      currentMember,
      initialTask: task,
      onSaved: () => {
        modal.close();
        showP7Toast('タスクを更新しました', 'success');
        if (s4Ctrl) s4Ctrl.onShow({ reload: true });
        loadActionData().then(refreshActionSections).catch(() => {});
      },
      onDeleted: () => {
        modal.close();
        showP7Toast('タスクを削除しました', 'success');
        if (s4Ctrl) s4Ctrl.onShow({ reload: true });
        loadActionData().then(refreshActionSections).catch(() => {});
      },
    });
    modal.setCtrl(ctrl);
  } catch (e) {
    console.error('[チバリヨ P7] タスク編集フォーム表示失敗:', e);
    modal.close();
    showP7Toast('編集画面の表示に失敗しました', 'error');
  }
}

// =====================================================================
// モーダル共通
// =====================================================================
function openModal({ title, width }) {
  if (activeModal) return null; // 多重防止

  const overlay = document.createElement('div');
  overlay.className = 'p7-modal-overlay';
  overlay.innerHTML = `
    <div class="p7-modal" style="max-width:${width || 520}px;">
      <div class="p7-modal-header">
        <h3 class="p7-modal-title">${escapeHtml(title)}</h3>
        <button type="button" class="p7-modal-close" aria-label="閉じる">×</button>
      </div>
      <div class="p7-modal-body"></div>
    </div>
  `;
  document.body.appendChild(overlay);

  const bodyEl = overlay.querySelector('.p7-modal-body');
  let ctrl = null;

  function close() {
    try { ctrl && ctrl.destroy && ctrl.destroy(); } catch (e) { /* noop */ }
    if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
    activeModal = null;
  }

  function tryClose() {
    if (ctrl && ctrl.isDirty && ctrl.isDirty()) {
      const ok = window.confirm('入力内容を破棄してもよろしいですか?');
      if (!ok) return;
    }
    close();
  }

  overlay.querySelector('.p7-modal-close').addEventListener('click', tryClose);
  overlay.addEventListener('click', (ev) => {
    if (ev.target === overlay) tryClose();
  });

  const modal = {
    bodyEl,
    close,
    tryClose,
    setCtrl: (c) => { ctrl = c; },
  };
  activeModal = modal;
  return modal;
}

function closeActiveModal(force) {
  if (!activeModal) return;
  if (force) activeModal.close();
  else activeModal.tryClose();
}

// =====================================================================
// KPI / ペース計算 (P1 / S1 / S5 と同一ロジック。4箇所目の重複実装)
// =====================================================================
function actualsOf(memberIds) {
  const idSet = new Set(memberIds);
  const rows = monthReports.filter((r) => idSet.has(r.memberId));
  return {
    appointment: rows.filter((r) => r.type === '訪問' && r.hasAppointment === true).length,
    quote: rows.filter((r) => r.quote === true).length,
    visit: rows.filter((r) => r.type === '訪問').length,
  };
}

function targetsOf(memberIds) {
  const out = { appointmentTarget: 0, quoteTarget: 0 };
  for (const id of memberIds) {
    const g = monthGoals.get(id);
    if (!g) continue;
    out.appointmentTarget += g.appointmentTarget;
    out.quoteTarget += g.quoteTarget;
  }
  return out;
}

function computePacePts(actuals, goal, progress) {
  const expectedRatio = progress.total > 0 ? progress.elapsed / progress.total : 0;
  const apptPacePt = goal.appointmentTarget > 0
    ? ((actuals.appointment / goal.appointmentTarget) - expectedRatio) * 100
    : null;
  const quotePacePt = goal.quoteTarget > 0
    ? ((actuals.quote / goal.quoteTarget) - expectedRatio) * 100
    : null;
  let minPt = Infinity;
  if (apptPacePt !== null) minPt = Math.min(minPt, apptPacePt);
  if (quotePacePt !== null) minPt = Math.min(minPt, quotePacePt);
  if (minPt === Infinity) minPt = null;
  return { apptPacePt, quotePacePt, minPt };
}

function computeMemberPace(memberId) {
  const progress = computeBusinessDayProgress();
  const actuals = actualsOf([memberId]);
  const goal = monthGoals.get(memberId) || { appointmentTarget: 0, quoteTarget: 0 };
  const pace = computePacePts(actuals, goal, progress);
  const status = pace.minPt === null ? 'unknown' : paceStatus(pace.minPt);
  return { ...pace, status };
}

function computeBusinessDayProgress() {
  const now = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  let elapsed = 0;
  let total = 0;
  for (let d = new Date(monthStart); d <= monthEnd; d.setDate(d.getDate() + 1)) {
    if (isBusinessDay(d)) {
      total += 1;
      if (d <= now) elapsed += 1;
    }
  }
  return { elapsed, total };
}

function isBusinessDay(d) {
  const dow = d.getDay();
  if (dow === 0 || dow === 6) return false;
  if (holidayDates.has(formatDate(d))) return false;
  return true;
}

function paceStatus(pt) {
  const danger = configData.alertPacePtThreshold ?? -15;
  const warn = configData.paceThresholdGood ?? -5;
  if (pt >= warn) return 'good';
  if (pt >= danger) return 'warn';
  return 'danger';
}

function paceLabel(status) {
  switch (status) {
    case 'good':    return '順調';
    case 'warn':    return '注意';
    case 'danger':  return '危険';
    case 'unknown': return '目標未設定';
    default:        return '—';
  }
}

function renderProgressBar(actual, target) {
  if (!target || target === 0) {
    return '<div class="p7-bar"><div class="p7-bar-fill is-empty"></div></div>';
  }
  const pct = Math.min(100, Math.round(actual / target * 100));
  let cls = 'is-low';
  if (pct >= 100) cls = 'is-complete';
  else if (pct >= 75) cls = 'is-high';
  else if (pct >= 50) cls = 'is-mid';
  return `<div class="p7-bar"><div class="p7-bar-fill ${cls}" style="width:${pct}%"></div></div>`;
}

// =====================================================================
// トースト (P7 独自。モバイルシェルの toast-wrap に依存しない)
// =====================================================================
function showP7Toast(message, type = 'info') {
  const wrap = document.getElementById('p7-toast-wrap');
  if (!wrap) return;

  const toast = document.createElement('div');
  toast.className = `p7-toast is-${type}`;
  toast.textContent = message;
  wrap.appendChild(toast);

  requestAnimationFrame(() => toast.classList.add('is-visible'));
  setTimeout(() => {
    toast.classList.remove('is-visible');
    setTimeout(() => {
      if (toast.parentNode) toast.parentNode.removeChild(toast);
    }, 200);
  }, 2400);
}

// =====================================================================
// ユーティリティ
// =====================================================================
function updateTimestamp() {
  const el = document.getElementById('p7-updated');
  if (!el) return;
  const now = new Date();
  const hh = String(now.getHours()).padStart(2, '0');
  const mm = String(now.getMinutes()).padStart(2, '0');
  el.textContent = `最終更新: ${hh}:${mm}`;
}

function showError(msg) {
  const el = document.getElementById('p7-error');
  if (!el) return;
  el.textContent = msg;
  el.style.display = 'block';
  setTimeout(() => { el.style.display = 'none'; }, 6000);
}

function formatDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

function formatYearMonth(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
}

function formatRate(actual, target) {
  if (!target || target === 0) return '—';
  return `${Math.round(actual / target * 100)}%`;
}

function formatPt(pt) {
  if (pt === null || pt === undefined) return '—';
  const sign = pt >= 0 ? '+' : '';
  return `${sign}${pt.toFixed(1)}pt`;
}

function escapeHtml(str) {
  return String(str ?? '').replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  }[c]));
}
